import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import { MaritalStatus, Purpose } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import * as Sentry from '@sentry/browser';
import { addDebtTypes } from '../../../../shared/mutators/addDebtTypes';
import {
  reloadFrontendToggles,
  saveFrontendToggles,
} from '../../../../shared/mutators/frontendToggles';
import { removeCoAppData } from '../../../../shared/mutators/removeCoAppData';
import { transformIncome } from '../../../../shared/mutators/transformIncomes';
import {
  mapNumberInputsToString,
  mapRadioInputsToBoolean,
  mapRadioInputsToString,
  mapStringInputsToNumber,
} from '../../../../utils';
import type { NorwayFormData } from '../FormData.no.types';

const frontendToggles = [
  'Frontend/IsNorwegian',
  'Frontend/CoApplicantIsNorwegian',
  'LoanApplication/CoApplicantIsCohabitant',
  'Frontend/HasCarDebt',
] as const;
const booleanRadioInputs = ['LoanApplication/CoApplicantIsCohabitant'] as const;
const numericStringInputs = [
  'LoanApplication/GrossIncomeYearly',
  'LoanApplication/PartnerGrossIncomeYearly',
  'LoanApplication/ExtraIncomeChildSupport',
  'LoanApplication/ExtraIncomeRental',
  'LoanApplication/RentCostMonthly',
  'LoanApplication/CoApplicantGrossIncomeYearly',
  'Debt/Personal/OutstandingAmount',
  'Debt/Personal/RefinanceAmount',
  'Debt/Housing/OutstandingAmount',
  'Debt/Student/OutstandingAmount',
  'Debt/CarMCBoat/OutstandingAmount',
] as const;

const staticValues = {
  'LoanApplication/MarketCountry': MarketCountryCode.Norway,

  'Person/MarketCountry': MarketCountryCode.Norway,
  'CoPerson/MarketCountry': MarketCountryCode.Norway,

  'Customer/MarketCountry': MarketCountryCode.Norway,
  'Customer/MobilePhoneCountryCode': 47,

  'CoCustomer/MarketCountry': MarketCountryCode.Norway,
  'CoCustomer/MobilePhoneCountryCode': 47,
};

// Map data from Backend Values to Frontend Values
const fromApi = (data: Record<string, unknown>): NorwayFormData => ({
  ...data,
  // Shared
  ...mapNumberInputsToString(data, numericStringInputs),
  ...mapRadioInputsToString(data, booleanRadioInputs),
  ...reloadFrontendToggles(data),
  ...transformIncome(data, 'fromApi'),
});

// TODO: remove when we are using the new consent service
// note, consent is a part of customer, but is handled in a separate handler
const addConsentFields = (data: NorwayFormData) => {
  const result = {};

  if (data['Ignore/MarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/MarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('no'),
        Partners: getMarketingConsentPartners('no'),
        Services: getMarketingConsentServices('no'),
      },
    });
  }

  if (data['Ignore/CoApplicantMarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/CoApplicantMarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('no'),
        Partners: getMarketingConsentPartners('no'),
        Services: getMarketingConsentServices('no'),
      },
    });
  }

  return result;
};

// Map data from Frontend Values to Backend Values
const toApi = (data: NorwayFormData): NorwayFormData => ({
  ...data,
  // Shared
  ...addDebtTypes(data),
  ...mapRadioInputsToBoolean(data, booleanRadioInputs),
  ...mapStringInputsToNumber(data, numericStringInputs),
  ...saveFrontendToggles(data, frontendToggles),
  ...transformIncome(data, 'toApi'),
  ...removeCoAppData(data),
  // Market specific
  ...staticValues,
  ...addConsentFields(data),
  ...addPurpose(data),
  ...addCitizenshipValues(data),
  ...addCoAppPartnerIncome(data),
});

const addPurpose = (data: NorwayFormData) => {
  const result = {};

  if (
    'LoanApplication/InitialPurpose' in data &&
    !data['LoanApplication/Purpose']
  ) {
    Object.assign(result, {
      'LoanApplication/Purpose': data['LoanApplication/InitialPurpose'],
    });
  }

  return result;
};

const addCitizenshipValues = (data: NorwayFormData) => {
  const result = {};

  const isNorwegian = data['Frontend/IsNorwegian'] === 'Yes';

  Object.assign(result, {
    'LoanApplication/Citizenship': isNorwegian
      ? MarketCountryCode.Norway
      : data['LoanApplication/Citizenship'],
  });

  if (data['Frontend/AddCoApplicant'] === 'Yes') {
    const isNorwegian = data['Frontend/CoApplicantIsNorwegian'] === 'Yes';

    Object.assign(result, {
      'LoanApplication/CoApplicantCitizenship': isNorwegian
        ? MarketCountryCode.Norway
        : data['LoanApplication/CoApplicantCitizenship'],
    });
  }

  return result;
};

const addCoAppPartnerIncome = (data: NorwayFormData) => {
  const result = {};

  if (
    !data['LoanApplication/CoApplicantIsCohabitant'] ||
    !data['LoanApplication/MaritalStatus']
  )
    return result;

  const isPartners = [MaritalStatus.Married, MaritalStatus.Cohabiting].includes(
    data['LoanApplication/MaritalStatus']
  );

  // @ts-expect-error Conversion from string to boolean is done on another function
  if (data['LoanApplication/CoApplicantIsCohabitant'] === 'Yes' && isPartners) {
    const grossIncome = Number(data['LoanApplication/GrossIncomeYearly']);
    const partnerIncome = Number(
      data['LoanApplication/PartnerGrossIncomeYearly']
    );
    Object.assign(result, {
      'LoanApplication/CoApplicantMaritalStatus':
        data['LoanApplication/MaritalStatus'],
      'LoanApplication/CoApplicantGrossIncomeYearly': partnerIncome ?? 0,
      'LoanApplication/CoApplicantPartnerGrossIncomeYearly': grossIncome ?? 0,
    });
  }

  return result;
};

const toApiLastSync = (data: NorwayFormData) => {
  const result = {};

  const purpose = data['LoanApplication/Purpose'];
  const personalDebt = data['Debt/Personal/OutstandingAmount'];
  const refinancingAmount = data['LoanApplication/TotalRefinancingAmount'];

  if (purpose === Purpose.RefinancingLoan && personalDebt && personalDebt > 0) {
    if (!refinancingAmount) {
      Sentry.captureEvent({
        message: 'Refinancing amount is missing',
        level: 'info',
        extra: {
          purpose,
          personalDebt,
          refinancingAmount,
        },
      });

      Object.assign(result, {
        'LoanApplication/TotalRefinancingAmount': personalDebt,
        'Debt/Personal/RefinanceAmount': personalDebt,
      });
    }
  }

  return result;
};

export const mutators = {
  fromApi,
  toApi,
  toApiLastSync,
};
